// src/modules/competitions/components/ImageUpload.tsx

import React, { useState, useRef } from 'react';
import Card from '../../../shared/components/ui/Card';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import { CompetitionImageType } from '../types';
import { competitionService } from '../services/competitionService';
import Button from '../../../shared/components/ui/Button';
import '../styles/participantStatus.css';

interface ImageUploadProps {
  imageType: CompetitionImageType;
  participationId: number;
  existingImage?: {
    file_name: string;
    uploaded_at: string;
  } | null;
  onUpload: (file: File, imageTypeId: number) => Promise<void>;
  isUploading?: boolean;
  error?: string | null;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  imageType,
  participationId,
  existingImage,
  onUpload,
  isUploading = false,
  error = null
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    const validation = competitionService.validateImageFile(
      file, 
      imageType.allowed_formats, 
      imageType.max_file_size_mb
    );

    if (!validation.isValid) {
      setValidationError(validation.error || 'Archivo no válido');
      setSelectedFile(null);
      setPreview(null);
      return;
    }

    setValidationError(null);
    setSelectedFile(file);

    // Crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      await onUpload(selectedFile, imageType.id);
      // Limpiar selección después de subir exitosamente
      setSelectedFile(null);
      setPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="image-upload-card" variant="elevated">
      <div className="image-upload-header">
        <h4 className="image-type-title">
          {imageType.name}
          {imageType.is_required && <span className="required-badge">Obligatoria</span>}
        </h4>
        
        {imageType.description && (
          <p className="image-type-description">{imageType.description}</p>
        )}
      </div>

      {/* Instrucciones */}
      {imageType.instructions && (
        <div className="upload-instructions">
          <strong>Instrucciones:</strong> {imageType.instructions}
        </div>
      )}

      {/* Especificaciones técnicas */}
      <div className="upload-specs">
        <div className="spec-item">
          <span className="spec-label">Formatos aceptados:</span>
          <span className="spec-value">{imageType.allowed_formats.join(', ').toUpperCase()}</span>
        </div>
        <div className="spec-item">
          <span className="spec-label">Tamaño máximo:</span>
          <span className="spec-value">{imageType.max_file_size_mb}MB</span>
        </div>
      </div>

      {/* Imagen existente */}
      {existingImage && !selectedFile && (
        <div className="existing-image">
          <div className="existing-image-info">
            <span className="status-icon">✓</span>
            <div className="existing-details">
              <p className="existing-filename">{existingImage.file_name}</p>
              <p className="existing-date">Subida el {formatDate(existingImage.uploaded_at)}</p>
            </div>
          </div>
          <Button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={handleSelectFile}
            disabled={isUploading}
          >
            Cambiar imagen
          </Button>
        </div>
      )}

      {/* Errores */}
      {(error || validationError) && (
        <ErrorMessage 
          message={error || validationError || ''} 
          type="error"
        />
      )}

      {/* Área de subida */}
      {!existingImage || selectedFile ? (
        <div className="upload-area">
          {/* Zona de drop */}
          <div
            className={`drop-zone ${dragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleSelectFile}
          >
            {selectedFile ? (
              <div className="selected-file">
                {preview && (
                  <img src={preview} alt="Preview" className="file-preview" />
                )}
                <div className="file-info">
                  <p className="file-name">{selectedFile.name}</p>
                  <p className="file-size">{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
            ) : (
              <div className="drop-zone-content">
                <div className="upload-icon">📁</div>
                <p className="upload-text">
                  Arrastra una imagen aquí o <span className="upload-link">haz clic para seleccionar</span>
                </p>
                <p className="upload-hint">
                  {imageType.allowed_formats.join(', ').toUpperCase()} - Máximo {imageType.max_file_size_mb}MB
                </p>
              </div>
            )}
          </div>

          {/* Input oculto */}
          <input
            ref={fileInputRef}
            type="file"
            accept={imageType.allowed_formats.map(format => `.${format}`).join(',')}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {/* Botones de acción */}
          {selectedFile && (
            <div className="upload-actions">
              <Button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setSelectedFile(null);
                  setPreview(null);
                  setValidationError(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                disabled={isUploading}
              >
                Cancelar
              </Button>
              
              <Button
                type="button"
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={isUploading}
              >
                {isUploading ? 'Subiendo...' : 'Subir imagen'}
              </Button>
            </div>
          )}
        </div>
      ) : null}

      {/* Estado de carga */}
      {isUploading && (
        <div className="upload-loading">
          <Loading size="small" message="Subiendo imagen..." />
        </div>
      )}
    </Card>
  );
};

export default ImageUpload;